/*!
 * boxFx.js — screen()'s effects on one box: viz(s3, kaleido:6), viz(s3, hue:120)
 * © 2026 Rob Canning — GPLv3
 *
 *     viz(s3, kaleido:6, turn:30)      its drawing in 6 mirrored wedges round its middle
 *     viz(s3, mirror:x)                its left half onto its right (y, xy)
 *     viz(s3, tile:3)                  3 x 3 of it in its box
 *     viz(s3, hue:120, invert:1, contrast:1.6, saturate:2, bright:1.2, blur:4)
 *     viz(s3, pixel:8, warp:20, grain:80, split:4)
 *     viz(s3, warp:follow(s3.level, 0, 40))   any of them on a signal
 *     viz(s3, fx:off)                  all of them off (each: 0)
 *
 * The same names and units as screen() (screenFx.js), on what one uid draws
 * instead of the whole stage. The filters (hue ... split) are a CSS filter
 * on the box's drawing -- each voice's group, a trail painted on the stage
 * -- with its own SVG filters for pixel, warp and split. kaleido / mirror /
 * tile need the drawing as pixels: the box draws on its canvas (scope.js
 * forces one), hidden, and a canvas over it is painted from it each frame,
 * copy by copy (paintGeometry). On a shape, a cluster, a drawing: the
 * filters only.
 */

import { secPerBeat } from "../cues/beats.js";
import { releaseBinding } from "../control/paramBinding.js";
import { waveformsForElement } from "./waveform.js";
import { onReset } from "./teardown.js";

const NS = "http://www.w3.org/2000/svg";
export const FILTER_KEYS = ["hue", "invert", "contrast", "saturate", "bright", "blur", "pixel", "warp", "grain", "split"];
const GEOM_KEYS = ["kaleido", "turn", "mirror", "tile"];
export const FX_KEYS = [...FILTER_KEYS, ...GEOM_KEYS];

const num = (v, d = 0) => (Number.isFinite(Number(v)) ? Number(v) : d);
let seq = 0;

/** The SVG filters of the boxes, made once. */
function defs() {
  let d = document.getElementById("oscilla-box-fx-defs");
  if (d) return d;
  d = document.createElementNS(NS, "svg");
  d.id = "oscilla-box-fx-defs";
  d.setAttribute("style", "position:absolute;width:0;height:0;overflow:hidden");
  document.body.appendChild(d);
  return d;
}

const ch = (r, g, b) => `${r} 0 0 0 0  0 ${g} 0 0 0  0 0 ${b} 0 0  0 0 0 1 0`;

/** A box's own SVG filter of a kind (pixel / warp / split), made on first use. */
function boxFilter(st, kind) {
  const id = `${st.id}-${kind}`;
  let f = document.getElementById(id);
  if (f) return f;
  const markup = {
    pixel: `<filter id="${id}" x="0" y="0" width="1" height="1" color-interpolation-filters="sRGB">
        <feFlood x="0" y="0" width="1" height="1" result="dot"/>
        <feComposite in="dot" width="8" height="8" result="cell"/>
        <feTile in="cell" result="grid"/>
        <feComposite in="SourceGraphic" in2="grid" operator="in"/>
        <feMorphology operator="dilate" radius="4"/></filter>`,
    warp: `<filter id="${id}" x="-0.15" y="-0.15" width="1.3" height="1.3" color-interpolation-filters="sRGB">
        <feTurbulence type="fractalNoise" baseFrequency="0.0067" numOctaves="2" seed="7" result="noise"/>
        <feOffset in="noise" dx="0" dy="0" result="drift"/>
        <feDisplacementMap in="SourceGraphic" in2="drift" scale="0" xChannelSelector="R" yChannelSelector="G"/></filter>`,
    split: `<filter id="${id}" x="-0.05" y="0" width="1.1" height="1" color-interpolation-filters="sRGB">
        <feOffset in="SourceGraphic" dx="0" result="left"/>
        <feColorMatrix in="left" type="matrix" values="${ch(1, 0, 0)}" result="r2"/>
        <feColorMatrix in="SourceGraphic" type="matrix" values="${ch(0, 1, 0)}" result="g"/>
        <feOffset in="SourceGraphic" dx="0" result="right"/>
        <feColorMatrix in="right" type="matrix" values="${ch(0, 0, 1)}" result="b2"/>
        <feComposite in="r2" in2="g" operator="arithmetic" k2="1" k3="1" result="rg"/>
        <feComposite in="rg" in2="b2" operator="arithmetic" k2="1" k3="1"/></filter>`,
  }[kind];
  defs().insertAdjacentHTML("beforeend", markup);
  return document.getElementById(id);
}

/** Screen pixels per unit of `el` (the filters' values are px on the screen). */
function unitsPerPx(el) {
  const m = el?.getScreenCTM?.();
  const s = m ? Math.hypot(m.a, m.b) : 1;
  return s > 0 ? 1 / s : 1;
}

/**
 * The CSS filter for a box's effects now ("" for none). `el` is what it
 * goes on (an SVG group: its units for the SVG filters' sizes).
 */
function chain(st, el) {
  const v = st.values, u = unitsPerPx(el), parts = [];
  st.u = u;                         // for the warp's drift (warpFrame)
  const warp = Math.abs(num(v.warp)), split = num(v.split);
  if (warp > 0) {
    const f = boxFilter(st, "warp");
    const scale = Math.min(400, warp) * u;
    f.querySelector("feDisplacementMap").setAttribute("scale", String(scale));
    // room round the box for what is pulled in from as far as scale/2: a
    // margin short of it took in nothing there -- gaps along the lines
    let b = null;
    try { b = el?.getBBox?.(); } catch { /* not drawn yet */ }
    // a word on the stage (an HTML element): its box on the screen, in px as u is
    if (!b && el?.getBoundingClientRect) b = el.getBoundingClientRect();
    if (b?.width > 0 && b.height > 0) {
      const mx = Math.max(0.15, (0.6 * scale) / b.width), my = Math.max(0.15, (0.6 * scale) / b.height);
      f.setAttribute("x", String(-mx)); f.setAttribute("width", String(1 + 2 * mx));
      f.setAttribute("y", String(-my)); f.setAttribute("height", String(1 + 2 * my));
    }
    f.querySelector("feTurbulence").setAttribute("baseFrequency", String(1 / (Math.max(8, num(v.grain, 150) || 150) * u)));
    parts.push(`url(#${f.id})`);
  }
  if (split) {
    const f = boxFilter(st, "split"), d = Math.max(-200, Math.min(200, split)) * u;
    const [r, b] = f.querySelectorAll("feOffset");
    r.setAttribute("dx", String(-d)); b.setAttribute("dx", String(d));
    parts.push(`url(#${f.id})`);
  }
  if (num(v.hue)) parts.push(`hue-rotate(${num(v.hue)}deg)`);
  if (num(v.invert)) parts.push(`invert(${Math.min(1, Math.max(0, num(v.invert)))})`);
  if (v.contrast != null && num(v.contrast, 1) !== 1) parts.push(`contrast(${Math.max(0, num(v.contrast, 1))})`);
  if (v.saturate != null && num(v.saturate, 1) !== 1) parts.push(`saturate(${Math.max(0, num(v.saturate, 1))})`);
  if (v.bright != null && num(v.bright, 1) !== 1) parts.push(`brightness(${Math.max(0, num(v.bright, 1))})`);
  if (num(v.blur) > 0) parts.push(`blur(${+Math.min(60, num(v.blur)).toFixed(2)}px)`);
  if (num(v.pixel) >= 2) {
    const f = boxFilter(st, "pixel"), s = Math.max(2, Math.round(num(v.pixel))) * u, h = s / 2;
    f.querySelector("feFlood").setAttribute("x", h);
    f.querySelector("feFlood").setAttribute("y", h);
    const c = f.querySelector("feComposite[in=dot]");
    c.setAttribute("width", s); c.setAttribute("height", s);
    f.querySelector("feMorphology").setAttribute("radius", h);
    parts.push(`url(#${f.id})`);
  }
  return parts.join(" ");
}

/** kaleido / mirror / tile now: "kaleido:6:30", "mirror:x", "tile:3", or "". */
export function geometrySpec(v) {
  const k = Math.round(num(v.kaleido));
  if (k >= 2) return `kaleido:${Math.min(24, k)}:${num(v.turn)}`;
  const m = String(v.mirror ?? "").toLowerCase();
  if (["x", "y", "xy"].includes(m)) return `mirror:${m}`;
  const t = Math.round(num(v.tile));
  if (t >= 2) return `tile:${Math.min(8, t)}`;
  return "";
}

// ---- the state of each box ------------------------------------------------

const boxes = new Map();          // element -> { id, values, kind, targets }

/**
 * Set one effect of what `el` is (kind "box": a sound's box; else a shape,
 * a cluster, a drawing) to `value`. `targets()` gives the elements the
 * filter goes on (a box: each voice's group and what it paints on the stage).
 * `wear(filter)`, given, puts the filter on instead (a text: its words now
 * and to come, text.js); `el` is then a key, { id, isConnected: true }.
 */
export function setBoxFx(el, kind, key, value, targets, wear = null) {
  dropGoneBoxes();
  let st = boxes.get(el);
  if (!st) boxes.set(el, st = { id: `oscilla-boxfx-${++seq}`, values: {}, kind, targets });
  st.targets = targets;
  st.wear = wear;
  if (value == null || String(value).toLowerCase() === "off") delete st.values[key];
  else st.values[key] = value;
  applyBoxFx(el);
}

/**
 * Boxes no longer on the page -- a sound hushed and run again has a new
 * box -- let go, and their filters with them. (Not their bindings: those
 * are keyed by the uid, which the new box carries on. A text's key is
 * never disconnected.)
 */
function dropGoneBoxes() {
  for (const [el, st] of boxes) {
    if (el.isConnected) continue;
    for (const f of document.querySelectorAll(`#oscilla-box-fx-defs filter[id^="${st.id}-"]`)) f.remove();
    boxes.delete(el);
  }
}

/** All of `el`'s effects off. */
export function clearBoxFx(el) {
  const st = boxes.get(el);
  if (!st) return;
  st.values = {};
  applyBoxFx(el);
}

/**
 * Every box's and shape's effects off, and what followed a signal for them
 * let go -- still(all) / Ctrl+Shift+, : the screen back to plain.
 */
export function clearAllBoxFx() {
  for (const [el, st] of [...boxes]) {
    for (const k of FX_KEYS) releaseBinding(`viz-fx:${el.id}:${k}`);
    st.values = {};
    if (el.isConnected) applyBoxFx(el);
    // a box not drawing (its sound stopped) is not ticking to put its copies away
    for (const h of waveformsForElement(el)) dropGeometry(h);
    boxes.delete(el);
  }
  for (const f of document.querySelectorAll("#oscilla-box-fx-defs filter")) f.remove();
}
onReset("stage", clearAllBoxFx);

/** The effects `el` has on now, by key. */
export const boxFxKeys = el => Object.keys(boxes.get(el)?.values || {});

/** What `el` has of `key` now (for viz(s3, warp:*2)). */
export const boxFxValue = (el, key) => boxes.get(el)?.values[key];

/** The filter (and, on a box, its geometry) laid on again: after a change, or a new voice. */
export function applyBoxFx(el) {
  const st = boxes.get(el);
  if (!st) return;
  const targets = st.targets?.() || [el];
  const f = chain(st, targets[0] || el);
  if (st.kind === "box") {
    // kept on the box: a voice to come, a trail painted on the stage (scope.js)
    if (f) el.dataset.vizFilter = f; else delete el.dataset.vizFilter;
    const g = geometrySpec(st.values);
    if (g) el.dataset.vizGeom = g; else delete el.dataset.vizGeom;
  } else if (geometrySpec(st.values)) {
    console.warn("[viz] kaleido, mirror and tile: on a sound's box (screen() for the whole stage)");
  }
  if (st.wear) st.wear(f);
  else for (const t of targets) if (t && t.style.filter !== f) t.style.filter = f;
  warpLoop();
}

// ---- warp: the ripples flow, as screen()'s do -------------------------------

let warpRaf = 0;
function warpLoop() {
  if (warpRaf) return;
  const any = [...boxes.values()].some(st => Math.abs(num(st.values.warp)) > 0);
  if (any) warpRaf = requestAnimationFrame(warpFrame);
}
function warpFrame(now) {
  warpRaf = 0;
  let any = false;
  const beat = 8000 * secPerBeat();          // once round every 8 beats
  for (const [el, st] of boxes) {
    if (!(Math.abs(num(st.values.warp)) > 0)) continue;
    if (!el.isConnected) { boxes.delete(el); continue; }
    any = true;
    const f = document.getElementById(`${st.id}-warp`);
    const o = f?.querySelector("feOffset");
    if (!o) continue;
    // as far round as screen()'s: a tenth of the window, in the box's units --
    // the box's own size made the flow slower the smaller it was (half, often)
    const c = document.getElementById("scoreContainer") || document.documentElement;
    const r = 0.1 * Math.min(c.clientWidth || 800, c.clientHeight || 600) * (st.u || 1);
    const a = (2 * Math.PI * now) / beat;
    o.setAttribute("dx", (r * Math.cos(a)).toFixed(1));
    o.setAttribute("dy", (r * Math.sin(a)).toFixed(1));
  }
  if (any) warpRaf = requestAnimationFrame(warpFrame);
}

// ---- kaleido / mirror / tile: the box's canvas, copied ----------------------

/**
 * One frame of a box's geometry: `rec` is the canvas the box drew on this
 * frame ({ fo, canvas, area }), hidden; a canvas over it is painted with
 * the copies -- turned and clipped about the box's middle (`draw`, the box
 * in its units). `spec` is the box's data-viz-geom.
 */
export function paintGeometry(handle, rec, draw, spec) {
  if (!rec?.canvas || !rec.area) return;
  const src = rec.canvas;
  let geo = handle._geom;
  if (!geo || geo.parent !== rec.fo.parentNode) {
    geo?.fo.remove();
    const fo = document.createElementNS(NS, "foreignObject");
    fo.style.pointerEvents = "none";
    const cv = document.createElement("canvas");
    cv.style.cssText = "width:100%;height:100%;display:block";
    fo.appendChild(cv);
    rec.fo.after(fo);
    geo = handle._geom = { fo, canvas: cv, parent: rec.fo.parentNode, src: null };
  }
  if (geo.src && geo.src !== src) geo.src.style.visibility = "";
  geo.src = src;
  src.style.visibility = "hidden";
  for (const a of ["x", "y", "width", "height"]) {
    const v = rec.fo.getAttribute(a);
    if (geo.fo.getAttribute(a) !== v) geo.fo.setAttribute(a, v);
  }
  const cv = geo.canvas, W = src.width, H = src.height;
  if (cv.width !== W || cv.height !== H) { cv.width = W; cv.height = H; }
  const g = cv.getContext("2d");
  g.clearRect(0, 0, W, H);
  // the box, in this canvas's pixels
  const { x: ax, y: ay, sx, sy } = rec.area;
  const bx = (draw.x - ax) * sx, by = (draw.y - ay) * sy, bw = draw.width * sx, bh = draw.height * sy;
  const cx = bx + bw / 2, cy = by + bh / 2, R = 2 * Math.hypot(W, H, bw, bh);
  const [mode, a, b] = spec.split(":");
  if (mode === "kaleido") {
    const n = Number(a), half = Math.PI / n, turn = (Number(b) - 90) * Math.PI / 180;
    for (let k = 0; k < n; k++) {
      g.save();
      g.translate(cx, cy);
      g.rotate(k * 2 * Math.PI / n);
      // every other wedge mirrored across its axis: the edges meet
      if (k % 2) { g.rotate(turn); g.scale(1, -1); g.rotate(-turn); }
      g.beginPath();
      g.moveTo(0, 0);
      g.lineTo(R * Math.cos(turn - half), R * Math.sin(turn - half));
      g.lineTo(R * Math.cos(turn + half), R * Math.sin(turn + half));
      g.closePath();
      g.clip();
      g.drawImage(src, -cx, -cy);
      g.restore();
    }
  } else if (mode === "mirror") {
    const mx = a.includes("x"), my = a.includes("y");
    const flips = [[1, 1]];
    if (mx) flips.push([-1, 1]);
    if (my) flips.push([1, -1]);
    if (mx && my) flips.push([-1, -1]);
    for (const [fx, fy] of flips) {
      g.save();
      g.translate(cx, cy); g.scale(fx, fy); g.translate(-cx, -cy);
      g.beginPath();
      g.rect(-R, -R, mx ? cx + R : 2 * R, my ? cy + R : 2 * R);   // the left / top half
      g.clip();
      g.drawImage(src, 0, 0);
      g.restore();
    }
  } else if (mode === "tile") {
    const n = Number(a);
    for (let i = 0; i < n; i++) for (let j = 0; j < n; j++) {
      g.save();
      g.translate(bx + i * bw / n, by + j * bh / n);
      g.scale(1 / n, 1 / n);
      g.translate(-bx, -by);
      g.beginPath(); g.rect(bx, by, bw, bh); g.clip();
      g.drawImage(src, 0, 0);
      g.restore();
    }
  }
}

/** A box's geometry put away: its canvas gone, the drawing shown again. */
export function dropGeometry(handle) {
  const geo = handle?._geom;
  if (!geo) return;
  geo.fo.remove();
  if (geo.src) geo.src.style.visibility = "";
  handle._geom = null;
}
